import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { arrowRotateCwIcon, filterClearIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { GridLoadingPanel } from 'components/GridLoadingPanel';
import SimpleDatePicker from 'components/SimpleDatePicker';
import LocalDateOnlyCell from 'components/cells/LocalDateOnlyCell';
import { RightAlignHeaderCell } from 'components/cells/RightAlignHeaderCell';
import { fetchApi } from 'services/api';
import { Title } from 'utils/title';
import { ViewModel } from 'TypeGen/AccountsReceivable/OutstandingInvoices/view-model';

const PageSize = 50;

type InvoiceFilters = {
  FromDate: Date | null;
  ToDate: Date | null;
};

type InvoiceRequest = InvoiceFilters & {
  Page: number;
};

const defaultFilters: InvoiceFilters = { FromDate: null, ToDate: null };

const OutstandingInvoices = ({ customerId }: { customerId: number }) => {
  const validCustomer = Number.isInteger(customerId) && customerId > 0;
  const [filters, setFilters] = useState<InvoiceFilters>(defaultFilters);
  const [request, setRequest] = useState<InvoiceRequest>({ ...defaultFilters, Page: 1 });
  const [result, setResult] = useState<ViewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pagingLimitReached, setPagingLimitReached] = useState(false);
  const requestSequence = useRef(0);

  const filterError = Boolean(filters.FromDate) !== Boolean(filters.ToDate)
    ? 'Supply both document dates or leave both blank.'
    : filters.FromDate && filters.ToDate && dayjs(filters.FromDate).isAfter(filters.ToDate, 'day')
      ? 'The From date must be on or before the To date.'
      : null;

  const fetchRecords = useCallback(async () => {
    if (!validCustomer) return;

    const requestID = ++requestSequence.current;
    setLoading(true);
    setLoadError(null);
    setPagingLimitReached(false);

    const parameters = new URLSearchParams({
      CustomerID: customerId.toString(),
      Page: request.Page.toString(),
      PageSize: PageSize.toString(),
    });
    if (request.FromDate && request.ToDate) {
      parameters.set('FromDate', dayjs(request.FromDate).format('YYYY-MM-DD'));
      parameters.set('ToDate', dayjs(request.ToDate).format('YYYY-MM-DD'));
    }

    try {
      const response: ViewModel = await fetchApi(`/api/AccountsReceivable/OutstandingInvoices?${parameters}`);
      if (requestID !== requestSequence.current) return;

      if (response.PagingLimitReached) {
        setPagingLimitReached(true);
        return;
      }
      setResult(response);
    } catch {
      if (requestID === requestSequence.current) {
        setLoadError('Unable to load outstanding invoices. Please retry.');
      }
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  }, [customerId, validCustomer, request]);

  useEffect(() => {
    const timer = window.setTimeout(() => fetchRecords(), 0);
    return () => {
      window.clearTimeout(timer);
      requestSequence.current += 1;
    };
  }, [fetchRecords]);

  const applyFilters = (value: InvoiceFilters) => {
    setResult(null);
    setRequest({ ...value, Page: 1 });
  };

  const invoices = result?.Invoices;
  const firstRecord = invoices?.Data.length ? (invoices.Page - 1) * invoices.PageSize + 1 : 0;
  const lastRecord = firstRecord ? firstRecord + invoices.Data.length - 1 : 0;

  return (
    <>
      <Title string="Outstanding Invoices" />
      <h4 className="mt-3">Outstanding Invoices</h4>
      {result && <div className="mb-3">{result.CustomerName} ({result.CustomerNumber})</div>}
      {!validCustomer && <div className="alert alert-danger" role="alert">Select a valid customer.</div>}
      {loadError && <div className="alert alert-danger" role="alert">
        {loadError} <Button disabled={loading} onClick={fetchRecords}>Retry</Button>
      </div>}
      {pagingLimitReached && <div className="alert alert-warning" role="alert">
        More invoices may exist, but this search has reached the paging limit. Select a smaller document-date range to continue.
      </div>}
      {filterError && <div className="alert alert-warning" role="alert">{filterError}</div>}
      {loading && <GridLoadingPanel />}
      <Grid
        data={invoices?.Data ?? []}
        resizable
        reorderable
        scrollable="scrollable"
        style={{ height: 'calc(100vh - 260px)', minHeight: 400 }}
      >
        <GridToolbar className="kendo-grid-toolbar-overflow-visible" overflow="none">
          <span>
            <label className="mb-1">Document Date Range:</label>
            <span className="d-flex" style={{ gap: 6, alignItems: 'center' }}>
              <SimpleDatePicker
                value={filters.FromDate}
                inputProps={{ placeholder: 'From' }}
                onChange={value => setFilters(current => ({ ...current, FromDate: value }))}
              />
              -
              <SimpleDatePicker
                value={filters.ToDate}
                inputProps={{ placeholder: 'To' }}
                onChange={value => setFilters(current => ({ ...current, ToDate: value }))}
              />
            </span>
          </span>
          <Button themeColor="primary" disabled={loading || !validCustomer || Boolean(filterError)} onClick={() => applyFilters(filters)}>
            Apply
          </Button>
          <Button
            svgIcon={filterClearIcon}
            title="Clear document dates"
            disabled={loading || !validCustomer}
            onClick={() => {
              setFilters(defaultFilters);
              applyFilters(defaultFilters);
            }}
          />
          <Button
            svgIcon={arrowRotateCwIcon}
            title="Refresh"
            disabled={loading || !validCustomer}
            onClick={() => setRequest(current => ({ ...current, Page: invoices?.Page ?? 1 }))}
          />
        </GridToolbar>
        <Column field="InvoiceNumber" title="Invoice Number" width={150} />
        <Column field="DocumentNumber" title="Document Number" width={180} />
        <Column field="DocumentDate" title="Document Date" width={150} cells={{ data: LocalDateOnlyCell }} />
        <Column field="PostingDate" title="Posting Date" width={150} cells={{ data: LocalDateOnlyCell }} />
        <Column field="DueDate" title="Due Date" width={150} cells={{ data: LocalDateOnlyCell }} />
        <Column field="CurrentAmount" title="Current Amount" width={160} format="{0:c2}" className="text-right" cells={{ headerCell: RightAlignHeaderCell }} />
        <Column field="AgingDays" title="Aging Days" width={120} className="text-right" cells={{ headerCell: RightAlignHeaderCell }} />
        <Column field="AgingBucket" title="Aging Bucket" width={160} />
      </Grid>
      <div className="d-flex align-items-center mt-2" style={{ gap: 12 }}>
        <Button
          disabled={loading || !invoices || invoices.Page <= 1}
          onClick={() => setRequest(current => ({ ...current, Page: invoices.Page - 1 }))}
        >Previous</Button>
        <span>Page {invoices?.Page ?? 1}</span>
        <Button
          disabled={loading || !invoices?.HasMore || pagingLimitReached}
          onClick={() => setRequest(current => ({ ...current, Page: invoices.Page + 1 }))}
        >Next</Button>
        {invoices && <span>{firstRecord ? `Showing invoices ${firstRecord}–${lastRecord}` : 'No outstanding invoices found.'}</span>}
      </div>
    </>
  );
};

const CustomerOutstandingInvoices = () => {
  const { customerId } = useParams<{ customerId: string }>();
  return <OutstandingInvoices key={customerId} customerId={Number(customerId)} />;
};

export default CustomerOutstandingInvoices;
